import React, { useState, useEffect, useRef } from 'react';
import { Send, Trash2, Bot, User, Loader2 } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../lib/api';
import { formatTime } from '../lib/utils';
import { toast } from 'sonner';

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChatHistory = async () => {
    try {
      const response = await chatAPI.getHistory();
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Optimistically add user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await chatAPI.sendMessage(userMessage);
      
      // Replace temp message and add assistant response
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [
          ...filtered,
          response.data.user_message,
          response.data.assistant_message
        ];
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear your chat history?')) return;
    
    try {
      await chatAPI.clearHistory();
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const WelcomeMessage = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#E8A095] to-[#D68C82] flex items-center justify-center mb-6">
        <Bot className="w-10 h-10 text-white" />
      </div>
      <h2 className="font-heading text-2xl text-[#4A2C2A] mb-3">
        Your AI Wellness Advisor
      </h2>
      <p className="text-[#4A2C2A]/70 max-w-md mb-8">
        I'm here to help you on your wellness journey. Ask me anything about 
        {user?.profile?.health_concerns?.length > 0 
          ? ' your health goals, nutrition, lifestyle changes, or get personalized advice.'
          : ' nutrition, wellness, lifestyle changes, or any health-related topics.'}
      </p>
      
      <div className="grid sm:grid-cols-2 gap-3 w-full max-w-lg">
        {[
          'What foods support gut health?',
          'How can I improve my sleep?',
          'Tips for reducing inflammation',
          'Morning wellness routine ideas'
        ].map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => {
              setInput(suggestion);
            }}
            className="p-4 text-left bg-white border border-[#E8A095]/20 rounded-xl text-sm text-[#4A2C2A]/80 hover:bg-[#E8A095]/5 hover:border-[#E8A095]/40 transition-all"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-12rem)] lg:h-[calc(100vh-10rem)]" data-testid="chat-page">
        {/* Chat Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E8A095] to-[#D68C82] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-medium text-[#4A2C2A]">Wellness Advisor</h2>
              <p className="text-xs text-[#8DA399]">Online • Personalized for you</p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearHistory}
              className="text-[#4A2C2A]/50 hover:text-[#D68282]"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear
            </Button>
          )}
        </div>

        {/* Messages Area */}
        <div className="flex-1 bg-white rounded-2xl border border-[#E8A095]/20 overflow-hidden flex flex-col">
          {initialLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="spinner" />
            </div>
          ) : messages.length === 0 ? (
            <WelcomeMessage />
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-end gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' 
                        ? 'bg-[#4A2C2A]' 
                        : 'bg-gradient-to-br from-[#E8A095] to-[#D68C82]'
                    }`}>
                      {message.role === 'user' 
                        ? <User className="w-4 h-4 text-white" />
                        : <Bot className="w-4 h-4 text-white" />
                      }
                    </div>
                    <div className={message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className={`text-xs mt-2 ${
                        message.role === 'user' ? 'text-white/60' : 'text-[#4A2C2A]/40'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-end gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E8A095] to-[#D68C82] flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="chat-bubble-assistant">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#E8A095]" />
                        <span className="text-[#4A2C2A]/60">Thinking...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-[#E8A095]/10 bg-[#FDFBF7]">
            <form onSubmit={handleSend} className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask your wellness advisor..."
                className="flex-1 input-warm"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!input.trim() || loading}
                className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl px-6"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
            <p className="text-xs text-[#4A2C2A]/40 mt-2 text-center">
              Your conversations are private. Not medical advice - consult healthcare professionals for medical concerns.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
